import ObjectId from 'bson-objectid';
import { Button, Col, Input, Row, SingleImageUploader, Text } from 'components';
import { Api, theme, uploadWithSignedUrl } from 'lib';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FiChevronDown } from 'react-icons/fi';
import styled from 'styled-components';
import EligibilitySection from './EligibilitySection';
import AvailabilityNotesEditor from './AvailabilityNotesEditor';

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid ${theme.GREY_20};
`;

const SelectWrapper = styled.div`
  width: 100%;
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  height: 43px;
  border-radius: 50px;
  border: ${(props) => (props.focused ? '2px' : '1px')} solid
    ${(props) => (props.focused ? '#000' : theme.GREY_50)};
  background: white;
  display: flex;
  align-items: center;
  padding: 0 16px;
  cursor: pointer;
  transition: border 0.15s ease;

  &:hover {
    border-color: ${theme.GREY_60};
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  font-family: CeraPro;
  font-size: 16px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 24px;

  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 16px;
  pointer-events: none;
  color: ${theme.GREY_60};
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-right: 8px;
`;

const TYPE_OPTIONS = [
  { value: 'raffle', label: 'Raffle' },
  { value: 'direct', label: 'Direct' }
];

const initialFormState = {
  name: '',
  nameJa: '',
  description: '',
  descriptionJa: '',
  detailedDescription: '',
  detailedDescriptionJa: '',
  image: null,
  type: 'raffle',
  eligibility: {
    bronze: { eligible: true, slots: 0, description: '', descriptionJa: '' },
    silver: { eligible: true, slots: 0, description: '', descriptionJa: '' },
    gold: { eligible: true, slots: 0, description: '', descriptionJa: '' }
  },
  displayOrder: 0,
  availabilityNotes: [],
  isActive: true
};

const RewardTypeForm = ({
  team,
  initialData = null,
  existingItems = [],
  isEditing = false,
  onSuccess,
  onCancel
}) => {
  const [form, setForm] = useState(initialData || initialFormState);
  const [imageData, setImageData] = useState(
    initialData?.image ? { url: initialData.image } : null
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    const order = parseInt(form.displayOrder) || 0;
    const conflict = existingItems.find(
      (i) => (i.displayOrder || 0) === order && i._id !== initialData?._id
    );
    if (conflict) {
      newErrors.displayOrder = `Order ${order} is already used by "${conflict.name}"`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      // Upload image if new file selected
      let imageUrl = '';
      if (imageData?.file) {
        const file = imageData.file;
        const extension = file.type?.split('/')[1] || 'jpeg';
        /*
          Keys must stay flat: the server persists media as a bare filename
          (see lib/files/mediaUrl.ts) and strips any prefix, so a nested key
          would be saved pointing at a path the object does not live at.
        */
        const key = `${ObjectId().toString()}.${extension}`;
        const url = await uploadWithSignedUrl(key, file.type, file);
        imageUrl = url || '';
      } else if (imageData?.url) {
        imageUrl = imageData.url;
      }

      const payload = {
        teamId: team._id,
        name: form.name,
        nameJa: form.nameJa,
        description: form.description,
        descriptionJa: form.descriptionJa,
        detailedDescription: form.detailedDescription,
        detailedDescriptionJa: form.detailedDescriptionJa,
        image: imageUrl,
        type: form.type,
        eligibility: form.eligibility,
        displayOrder: parseInt(form.displayOrder) || 0,
        availabilityNotes: form.availabilityNotes.filter((n) => n.text?.trim()),
        isActive: form.isActive
      };

      let res;
      if (isEditing && initialData?._id) {
        res = await Api.put('/admin/rewardType/update', {
          id: initialData._id,
          ...payload
        });
      } else {
        res = await Api.post('/admin/rewardType/create', payload);
      }

      if (res) {
        dispatch({
          type: 'HANDLE_RES',
          payload: { res: isEditing ? 'Reward type updated.' : 'Reward type created.' }
        });
        onSuccess?.();
      }
    } catch (error) {
      console.error('Failed to save reward type:', error);
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: 'Failed to save reward type. Please try again.' }
      });
    }

    setLoading(false);
  };

  return (
    <Col pad="0 24px 24px">
      {/* Basic Info */}
      <FormSection>
        <SectionTitle>
          <Text b1 med>Basic Information</Text>
        </SectionTitle>

        <Col gap="16px">
          <SingleImageUploader value={imageData} onChange={setImageData} />

          <Input
            title="Name *"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., VIP Tickets"
            error={errors.name}
          />

          <Input
            title="Name (Japanese)"
            value={form.nameJa}
            onChange={(e) => handleChange('nameJa', e.target.value)}
            placeholder="e.g., VIPチケット"
          />

          <SelectWrapper>
            <Text b2 med marg="0 0 4px 16px">Type</Text>
            <SelectContainer>
              <StyledSelect
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </StyledSelect>
              <SelectArrow>
                <FiChevronDown size={18} />
              </SelectArrow>
            </SelectContainer>
          </SelectWrapper>

          <Input
            title="Short Description"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description for list view..."
            error={errors.description}
          />

          <Input
            title="Short Description (Japanese)"
            value={form.descriptionJa}
            onChange={(e) => handleChange('descriptionJa', e.target.value)}
            placeholder="リスト表示用の簡単な説明..."
          />

          <Input
            title="Detailed Description"
            multiline
            ht="100px"
            value={form.detailedDescription}
            onChange={(e) => handleChange('detailedDescription', e.target.value)}
            placeholder="Full description for detail page..."
          />

          <Input
            title="Detailed Description (Japanese)"
            multiline
            ht="100px"
            value={form.detailedDescriptionJa}
            onChange={(e) => handleChange('detailedDescriptionJa', e.target.value)}
            placeholder="詳細ページ用の完全な説明..."
          />
        </Col>
      </FormSection>

      {/* Eligibility */}
      <FormSection>
        <SectionTitle>
          <Text b1 med>Eligibility by Badge Tier</Text>
        </SectionTitle>

        <EligibilitySection
          value={form.eligibility}
          onChange={(v) => handleChange('eligibility', v)}
        />
      </FormSection>

      {/* Availability Notes */}
      <FormSection>
        <SectionTitle>
          <Text b1 med>Availability Notes</Text>
        </SectionTitle>

        <AvailabilityNotesEditor
          value={form.availabilityNotes}
          onChange={(v) => handleChange('availabilityNotes', v)}
        />
      </FormSection>

      {/* Settings */}
      <FormSection>
        <SectionTitle>
          <Text b1 med>Settings</Text>
        </SectionTitle>

        <Row start gap="24px" center>
          <Col wid="150px">
            <Input
              title="Display Order"
              type="number"
              min="0"
              value={form.displayOrder}
              onChange={(e) => handleChange('displayOrder', e.target.value)}
              placeholder="0"
              error={errors.displayOrder}
            />
          </Col>

          <Row noFlex center marg="24px 0 0">
            <Checkbox
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
            />
            <Text b2>Active</Text>
          </Row>
        </Row>
      </FormSection>

      {/* Actions */}
      <Row pad="24px 0" gap="16px">
        <Button
          big
          text="Cancel"
          onClick={onCancel}
          bordered
          style={{ flex: 1 }}
        />
        <Button
          big
          text={loading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create')}
          onClick={handleSubmit}
          disabled={loading}
          bg={theme.SECONDARY}
          style={{ flex: 1 }}
        />
      </Row>
    </Col>
  );
};

export default RewardTypeForm;
